import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TEST_IDS } from '../constants/testIds';
import { usePlayer } from '../context/PlayerContext';
import { Play, Pause, Loader2, Instagram, Facebook } from 'lucide-react';

// WhatsApp uses its own brand mark. Lucide doesn't ship one, so inline the
// official glyph path so it renders alongside the lucide icons at the same
// visual weight (strokeWidth is emulated by the path itself).
const WhatsAppIcon = ({ size = 16, className, strokeWidth: _sw }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
  </svg>
);

const SOCIALS = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/osolondonradio',
    Icon: Instagram,
    testId: 'social-instagram',
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/share/1bNDmEZz5j/',
    Icon: Facebook,
    testId: 'social-facebook',
  },
  {
    label: 'WhatsApp',
    href: 'https://wa.me/447944131430',
    Icon: WhatsAppIcon,
    testId: 'social-whatsapp',
  },
];

const SocialLinks = ({ className = '', size = 16 }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    {SOCIALS.map(({ label, href, Icon, testId }) => (
      <a
        key={label}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`OSO London on ${label}`}
        data-testid={testId}
        className="opacity-70 hover:opacity-100 transition-opacity"
      >
        <Icon size={size} strokeWidth={1.5} />
      </a>
    ))}
  </div>
);

const NavLink = ({ to, label, testId }) => {
  const { pathname } = useLocation();
  const active = pathname === to || (to !== '/' && pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`block py-2 font-mono-r text-xs tracking-[0.25em] uppercase transition-opacity hover:opacity-60 ${active ? 'opacity-100' : 'opacity-70'}`}
    >
      <span className="inline-block w-4">{active ? '›' : ''}</span>{label}
    </Link>
  );
};

const miniIcon = (loading, playing) => {
  if (loading) return <Loader2 size={14} className="animate-spin" />;
  if (playing) return <Pause size={14} />;
  return <Play size={14} />;
};

const miniStatus = (playing, isLive) => {
  if (playing) return 'On Air';
  if (isLive) return 'Live';
  return 'Off Air';
};

const MiniPlayer = () => {
  const { playing, loading, toggle, nowPlaying } = usePlayer();
  return (
    <div className="hairline-t hairline-b px-4 py-3 flex items-center justify-between gap-3">
      <button
        data-testid="nav-mini-play-btn"
        onClick={toggle}
        aria-label={playing ? 'Pause stream' : 'Play stream'}
        className="w-9 h-9 hairline flex items-center justify-center hover:bg-white hover:text-black transition-colors"
      >
        {miniIcon(loading, playing)}
      </button>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-mono-r opacity-80">
        <span className={`inline-block w-2 h-2 bg-black ${playing ? 'pulse-dot' : 'opacity-40'}`} />
        {miniStatus(playing, nowPlaying.is_live)}
      </div>
    </div>
  );
};

export default function Layout({ children }) {
  const year = new Date().getFullYear();
  return (
    <div className="grain scanlines min-h-screen flex flex-col items-center bg-white text-black">
      <div className="w-full max-w-[1100px] mx-auto px-6 grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-10 py-10 flex-1 relative z-10">
        {/* Sidebar nav (centered column on mobile) */}
        <aside className="flex md:block flex-col items-center md:items-start text-center md:text-left">
          <Link to="/" data-testid={TEST_IDS.navLogo} className="block select-none">
            <img
              src="/oso-logo.png"
              alt="OSO London — house in the heart"
              className="w-[180px] md:w-full max-w-[220px] mx-auto md:mx-0"
            />
          </Link>

          <nav className="mt-8 w-full max-w-[220px] mx-auto md:mx-0 hairline-t hairline-b py-3">
            <NavLink to="/"          label="Home"     testId={TEST_IDS.navHome} />
            <NavLink to="/listen"    label="Listen"   testId={TEST_IDS.navListen} />
            <NavLink to="/schedule"  label="Schedule" testId={TEST_IDS.navSchedule} />
            <NavLink to="/shows"     label="Shows"    testId={TEST_IDS.navShows} />
            <NavLink to="/alexa"     label="Alexa"    testId={TEST_IDS.navAlexa} />
            <NavLink to="/about"     label="About"    testId={TEST_IDS.navAbout} />
            <NavLink to="/contact"   label="Contact"  testId={TEST_IDS.navContact} />
          </nav>

          <div className="mt-6 w-full max-w-[220px] mx-auto md:mx-0">
            <MiniPlayer />
          </div>

          <SocialLinks className="mt-5 w-full max-w-[220px] mx-auto md:mx-0 justify-center md:justify-start" size={16} />
        </aside>

        <main className="text-center md:text-left flex flex-col items-center md:items-stretch min-w-0 overflow-hidden">
          {children}
        </main>
      </div>

      {/* Footer */}
      <footer className="w-full hairline-t mt-10 relative z-10">
        <div className="max-w-[1100px] mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] font-mono-r tracking-[0.3em] uppercase">
          <span>© {year} OSO LONDON</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-link-privacy" className="hover:opacity-100 opacity-70">Privacy</Link>
            <Link to="/terms"   data-testid="footer-link-terms"   className="hover:opacity-100 opacity-70">Terms</Link>
            <Link to="/alexa"   data-testid="footer-link-alexa"   className="hover:opacity-100 opacity-70">Alexa</Link>
            <SocialLinks size={14} />
          </div>
          <span>BROADCASTING FROM LONDON</span>
        </div>
      </footer>
    </div>
  );
}
